import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { useSuspenseQuery, queryOptions } from "@tanstack/react-query";
import { MAX_LIST_PAGE, parsePageSearch } from "@/lib/search-params";
import { collectionPageSchema, safeJsonLd } from "@/lib/structured-data-core";
import { Header } from "@/components/site/Header";
import { NotFoundPage } from "@/components/site/NotFoundPage";
import { Footer } from "@/components/site/Footer";
import { listBlogPosts, listBlogTaxonomyMeta } from "@/lib/blog.functions";

const metaQO = (slug: string) =>
  queryOptions({
    queryKey: ["blog-category", slug],
    queryFn: () => listBlogTaxonomyMeta({ data: { kind: "category", slug } }),
  });

const postsQO = (slug: string, page: number) =>
  queryOptions({
    queryKey: ["blog-posts", { categorySlug: slug, page }],
    queryFn: () => listBlogPosts({ data: { categorySlug: slug, page } }),
  });

export const Route = createFileRoute("/category/$slug")({
  validateSearch: parsePageSearch,
  loaderDeps: ({ search }) => ({ page: search.page ?? 1 }),
  loader: async ({ context, params, deps }) => {
    if (deps.page > MAX_LIST_PAGE) throw notFound();
    const meta = await context.queryClient.ensureQueryData(metaQO(params.slug));
    if (!meta) throw notFound();
    const posts = await context.queryClient.ensureQueryData(postsQO(params.slug, deps.page));
    // A page past the last one has nothing to show. Answer 404, not an empty page.
    if (deps.page > 1 && posts.posts.length === 0) throw notFound();
    return meta;
  },
  head: ({ loaderData, params }) => {
    if (!loaderData)
      return {
        meta: [
          { title: "Page Not Found | RESTORE Hair" },
          { name: "robots", content: "noindex, follow" },
        ],
      };
    const name = loaderData.name;
    return {
      meta: [
        { title: `${name} | RESTORE Hair Blog` },
        { name: "description", content: `Articles in the ${name} category from RESTORE Hair.` },
        { name: "robots", content: "noindex, follow" },
      ],
      links: [{ rel: "canonical", href: `/category/${params.slug}/` }],
      scripts: [
        {
          type: "application/ld+json",
          children: safeJsonLd(collectionPageSchema(name, `Articles in the ${name} category from RESTORE Hair.`, `/category/${params.slug}`)),
        },
      ],
    };
  },
  component: CategoryPage,
  errorComponent: ({ error, reset }) => (
    <div className="p-10 text-center">Something went wrong.<button onClick={reset} className="ml-2 underline">Retry</button></div>
  ),
  notFoundComponent: () => <NotFoundPage />,
});

function CategoryPage() {
  const { slug } = Route.useParams();
  const { page } = Route.useSearch();
  const currentPage = page ?? 1;
  const { data: meta } = useSuspenseQuery(metaQO(slug));
  const { data } = useSuspenseQuery(postsQO(slug, currentPage));
  const totalPages = Math.max(1, Math.ceil(data.total / data.pageSize));

  return (
    <>
      <Header />
      <main className="mx-auto max-w-6xl px-4 py-12 lg:py-16">
        <header className="mb-10">
          <p className="text-sm font-semibold uppercase tracking-wide text-primary">Category</p>
          <h1 className="mt-1 text-3xl font-bold tracking-tight text-foreground lg:text-4xl">{meta?.name}</h1>
          {meta?.description ? <p className="mt-3 max-w-2xl text-base text-muted-foreground">{meta.description}</p> : null}
        </header>
        <PostsGrid posts={data.posts} />
        {totalPages > 1 ? (
          <Pagination basePath="/category/$slug/" params={{ slug }} page={currentPage} totalPages={totalPages} />
        ) : null}
      </main>
      <Footer />
    </>
  );
}

function PostsGrid({ posts }: { posts: Array<{ id: string; slug: string; title: string; excerpt: string | null; featured_image_url: string | null; published_at: string | null; author: { name: string; slug: string } | null }> }) {
  if (posts.length === 0) return <p className="text-muted-foreground">No posts in this category yet.</p>;
  return (
    <ul className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3">
      {posts.map((p) => (
        <li key={p.id} className="group flex flex-col overflow-hidden rounded-lg border border-border bg-card transition-shadow hover:shadow-lg">
          <Link to="/$slug/" params={{ slug: p.slug }} className="flex h-full flex-col">
            {p.featured_image_url ? (
              <div className="aspect-[16/9] overflow-hidden bg-muted">
                <img src={p.featured_image_url} alt={p.title} loading="lazy" className="h-full w-full object-cover transition-transform group-hover:scale-105" />
              </div>
            ) : null}
            <div className="flex flex-1 flex-col gap-2 p-5">
              <h2 className="text-lg font-semibold leading-snug text-foreground group-hover:text-primary">{p.title}</h2>
              {p.excerpt ? <p className="line-clamp-3 text-sm text-muted-foreground">{p.excerpt}</p> : null}
            </div>
          </Link>
        </li>
      ))}
    </ul>
  );
}

function Pagination({ basePath, params, page, totalPages }: { basePath: "/category/$slug/" | "/tag/$slug/" | "/author/$slug/"; params: { slug: string }; page: number; totalPages: number }) {
  return (
    <nav className="mt-12 flex items-center justify-center gap-2" aria-label="Pagination">
      {page > 1 ? (
        <Link to={basePath} params={params} search={{ page: page - 1 }} className="rounded-md border border-border px-3 py-2 text-sm hover:bg-accent">← Newer</Link>
      ) : null}
      <span className="px-3 py-2 text-sm text-muted-foreground">Page {page} of {totalPages}</span>
      {page < totalPages ? (
        <Link to={basePath} params={params} search={{ page: page + 1 }} className="rounded-md border border-border px-3 py-2 text-sm hover:bg-accent">Older →</Link>
      ) : null}
    </nav>
  );
}

function NotFound({ label }: { label: string }) {
  return (
    <>
      <Header />
      <main className="mx-auto max-w-4xl px-4 py-20 text-center">
        <h1 className="text-3xl font-semibold text-foreground">{label} not found</h1>
        <Link to="/hair-loss-blog/" className="mt-6 inline-block rounded-md bg-primary px-4 py-2 text-sm text-primary-foreground">All posts</Link>
      </main>
      <Footer />
    </>
  );
}